import { useState } from "react";

export default function ContactForm({ initialSubject = "" }) {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    subject: initialSubject,
    message: "",
    website_url: "", // Honeypot
  });

  const [status, setStatus] = useState({
    submitting: false,
    success: false,
    error: null,
  });

  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Navn skal udfyldes";
    
    if (!formData.phone.trim()) {
      newErrors.phone = "Telefonnummer skal udfyldes";
    } else if (!/^[0-9\s+]{8,15}$/.test(formData.phone.replace(/\s/g, ""))) {
      newErrors.phone = "Indtast venligst et gyldigt telefonnummer (mindst 8 tal)";
    }

    if (!formData.email.trim()) {
      newErrors.email = "E-mail skal udfyldes";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Indtast venligst en gyldig e-mailadresse";
    }

    if (!formData.message.trim()) newErrors.message = "Beskrivelse skal udfyldes";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setStatus({ submitting: true, success: false, error: null });

    try {
      const response = await fetch("https://api.dev.auxo.dk/webform/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          subject: formData.subject || "Kontakt fra hjemmeside",
          message: formData.message,
          website_url: formData.website_url, // Honeypot
        }),
      });

      if (response.ok) {
        setStatus({ submitting: false, success: true, error: null });
        setFormData({
          name: "",
          phone: "",
          email: "",
          subject: "",
          message: "",
          website_url: "",
        });
      } else {
        const errData = await response.json();
        throw new Error(errData.message || "Der skete en fejl under afsendelsen.");
      }
    } catch (err) {
      setStatus({
        submitting: false,
        success: false,
        error: err.message || "Kunne ikke oprette forbindelse til serveren. Prøv igen senere.",
      });
    }
  };

  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-6 shadow-sm md:p-8">
      {status.success ? (
        <div className="text-center py-8">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
            <i className="fa-solid fa-circle-check text-3xl" />
          </div>
          <h3 className="text-2xl font-bold text-brand-stone-dark">Tak for din besked!</h3>
          <p className="mt-2 text-stone-600">
            Mads vender tilbage til dig så hurtigt som muligt – typisk inden for få timer.
          </p>
          <button
            type="button"
            onClick={() => setStatus((prev) => ({ ...prev, success: false }))}
            className="mt-6 rounded-lg bg-brand-green px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-green-light"
          >
            Send en ny besked
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Honeypot field - ABSOLUTELY HIDDEN from humans */}
          <div className="sr-only absolute -z-50 overflow-hidden opacity-0" aria-hidden="true">
            <label htmlFor="website_url">Do not fill this field if you are human</label>
            <input
              id="website_url"
              type="text"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
            />
          </div>

          <div>
            <label htmlFor="name" className="block text-sm font-semibold text-brand-stone-dark">
              Navn <span className="text-brand-orange">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`mt-1 block w-full rounded-lg border px-4 py-3 text-stone-900 shadow-xs focus:border-brand-green focus:ring-1 focus:ring-brand-green focus:outline-hidden ${
                errors.name ? "border-red-500" : "border-stone-300"
              }`}
              placeholder="Dit fulde navn"
            />
            {errors.name && <p className="mt-1 text-xs text-red-500 font-medium">{errors.name}</p>}
          </div>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="phone" className="block text-sm font-semibold text-brand-stone-dark">
                Telefonnummer <span className="text-brand-orange">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={`mt-1 block w-full rounded-lg border px-4 py-3 text-stone-900 shadow-xs focus:border-brand-green focus:ring-1 focus:ring-brand-green focus:outline-hidden ${
                  errors.phone ? "border-red-500" : "border-stone-300"
                }`}
                placeholder="Tlf. nummer"
              />
              {errors.phone && <p className="mt-1 text-xs text-red-500 font-medium">{errors.phone}</p>}
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-brand-stone-dark">
                E-mail <span className="text-brand-orange">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`mt-1 block w-full rounded-lg border px-4 py-3 text-stone-900 shadow-xs focus:border-brand-green focus:ring-1 focus:ring-brand-green focus:outline-hidden ${
                  errors.email ? "border-red-500" : "border-stone-300"
                }`}
                placeholder="din@email.dk"
              />
              {errors.email && <p className="mt-1 text-xs text-red-500 font-medium">{errors.email}</p>}
            </div>
          </div>

          <div>
            <label htmlFor="subject" className="block text-sm font-semibold text-brand-stone-dark">
              Emne (valgfrit)
            </label>
            <select
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              className="mt-1 block w-full rounded-lg border border-stone-300 px-4 py-3 text-stone-900 shadow-xs focus:border-brand-green focus:ring-1 focus:ring-brand-green focus:outline-hidden bg-white"
            >
              <option value="">Vælg emne...</option>
              <option value="Book tid">Book tid til service/reparation</option>
              <option value="Dæk og hjul">Dæk og hjulskift</option>
              <option value="Synsklargøring">Synsklargøring</option>
              <option value="Andet">Andet spørgsmål</option>
            </select>
          </div>

          <div>
            <label htmlFor="message" className="block text-sm font-semibold text-brand-stone-dark">
              Beskrivelse <span className="text-brand-orange">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              rows="4"
              value={formData.message}
              onChange={handleChange}
              className={`mt-1 block w-full rounded-lg border px-4 py-3 text-stone-900 shadow-xs focus:border-brand-green focus:ring-1 focus:ring-brand-green focus:outline-hidden ${
                errors.message ? "border-red-500" : "border-stone-300"
              }`}
              placeholder="Beskriv hvad du skal have lavet, f.eks. bilmærke, model, årgang eller registreringsnummer..."
            />
            {errors.message && <p className="mt-1 text-xs text-red-500 font-medium">{errors.message}</p>}
          </div>

          {status.error && (
            <div className="rounded-lg bg-red-50 p-4 text-sm text-red-700 font-medium">
              <i className="fa-solid fa-triangle-exclamation mr-2" />
              {status.error}
            </div>
          )}

          <button
            type="submit"
            disabled={status.submitting}
            className="w-full rounded-lg bg-brand-orange px-6 py-3.5 text-center text-sm font-bold text-white shadow-xs transition hover:bg-brand-orange-dark focus:ring-4 focus:ring-green-300 focus:outline-hidden disabled:opacity-50 cursor-pointer"
          >
            {status.submitting ? (
              <span className="flex items-center justify-center gap-2">
                <i className="fa-solid fa-spinner animate-spin" />
                Sender besked...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <i className="fa-solid fa-paper-plane" />
                Send besked
              </span>
            )}
          </button>
        </form>
      )}
    </div>
  );
}
